import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, AlertCircle, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import type { Locale } from '../../i18n/types.js';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const { login } = useAuth();
  const { t, locale, setLocale, locales, currentLocaleInfo } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as any)?.from?.pathname || '/admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !secret) {
      setError(t('login.errorRequired'));
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      await login({ email, secret });
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || t('login.errorInvalid'));
    } finally {
      setIsLoading(false);
    }
  };

  const setPreset = (presetEmail: string) => {
    setEmail(presetEmail);
    setSecret('dev-admin-secret-key-min32chars');
  };

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-slate-100 dark:bg-obsidian-950 p-4 font-sans">
      <div className="w-full max-w-md bg-white dark:bg-obsidian-850 border border-slate-200 dark:border-obsidian-750 rounded-2xl shadow-xl p-8 space-y-6 relative">
        {/* Language Selector in Top Right */}
        <div className="absolute top-4 right-4">
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsLangOpen(!isLangOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg border border-slate-200 dark:border-obsidian-700 bg-slate-50 dark:bg-obsidian-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-obsidian-700 transition-colors"
            >
              <span>{currentLocaleInfo.flag}</span>
              <span className="font-semibold text-[11px]">{currentLocaleInfo.label}</span>
              <ChevronDown className="w-3 h-3 text-slate-400" />
            </button>

            {isLangOpen && (
              <div className="absolute right-0 mt-1 w-36 rounded-xl border border-slate-200 dark:border-obsidian-700 bg-white dark:bg-obsidian-800 shadow-xl py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                {locales.map((loc) => {
                  const isActive = loc.code === locale;
                  return (
                    <button
                      key={loc.code}
                      type="button"
                      onClick={() => {
                        setLocale(loc.code as Locale);
                        setIsLangOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-1.5 text-xs text-left transition-colors ${
                        isActive
                          ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-semibold'
                          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-obsidian-700'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <span>{loc.flag}</span>
                        <span>{loc.nativeName}</span>
                      </span>
                      {isActive && <Check className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Header */}
        <div className="text-center space-y-2 pt-2">
          <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center text-white mx-auto shadow-md">
            <Shield className="w-6 h-6" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
            {t('login.title')}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('login.subtitle')}
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-700 dark:text-rose-400 text-xs flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              {t('login.emailLabel')}
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.emailPlaceholder')}
              required
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              {t('login.secretLabel')}
            </label>
            <Input
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={t('login.secretPlaceholder')}
              required
            />
          </div>

          <Button type="submit" variant="primary" className="w-full mt-2" isLoading={isLoading}>
            {isLoading ? t('login.signingIn') : t('login.submitButton')}
          </Button>
        </form>

        {/* Demo Fast Roles Selector (for rapid local evaluation) */}
        <div className="pt-4 border-t border-slate-100 dark:border-obsidian-750 space-y-2">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider text-center">
            {t('login.devSwitcherTitle')}
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <button
              type="button"
              onClick={() => setPreset('super_admin@hisob.uz')}
              className="p-2 rounded-lg border border-slate-200 dark:border-obsidian-750 hover:bg-slate-50 dark:hover:bg-obsidian-800 text-slate-700 dark:text-slate-300 text-left font-mono text-[11px] transition-colors"
            >
              👑 super_admin
            </button>
            <button
              type="button"
              onClick={() => setPreset('support@hisob.uz')}
              className="p-2 rounded-lg border border-slate-200 dark:border-obsidian-750 hover:bg-slate-50 dark:hover:bg-obsidian-800 text-slate-700 dark:text-slate-300 text-left font-mono text-[11px] transition-colors"
            >
              🎧 support
            </button>
            <button
              type="button"
              onClick={() => setPreset('developer@hisob.uz')}
              className="p-2 rounded-lg border border-slate-200 dark:border-obsidian-750 hover:bg-slate-50 dark:hover:bg-obsidian-800 text-slate-700 dark:text-slate-300 text-left font-mono text-[11px] transition-colors"
            >
              💻 developer
            </button>
            <button
              type="button"
              onClick={() => setPreset('auditor@hisob.uz')}
              className="p-2 rounded-lg border border-slate-200 dark:border-obsidian-750 hover:bg-slate-50 dark:hover:bg-obsidian-800 text-slate-700 dark:text-slate-300 text-left font-mono text-[11px] transition-colors"
            >
              📋 auditor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
